// Retroactive translation dialog (TODO 13.6).
//
// Lets the makler pick which missing languages to add to an existing
// document. Re-fetches fresh client / property / offer rows from the
// API, then runs the 10-language orchestrator on the chosen subset.
// PDFs land in deterministic R2 keys (`documents/{id}/{lang}.pdf`),
// `available_languages` JSON is appended atomically.
//
// Data freshness: re-translation uses CURRENT data — if a client moved
// or a property price changed since the original DE was generated, the
// new translation reflects the new state. The footer disclaimer
// ("Die deutsche Fassung ist rechtlich verbindlich") covers this.

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2, AlertTriangle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  type DocumentRow,
  useDocumentRenderInput,
} from '@/hooks/data/useDocuments';
import { normalizeRenderInput } from '@/lib/renderInput';

const SUPPORTED: ReadonlyArray<string> = [
  'de', 'en', 'fr', 'it', 'sr', 'bg', 'ru', 'tr', 'ar', 'zh',
];

const LANG_LABEL: Record<string, string> = {
  de: 'Deutsch',
  en: 'Englisch',
  fr: 'Französisch',
  it: 'Italienisch',
  sr: 'Serbisch',
  bg: 'Bulgarisch',
  ru: 'Russisch',
  tr: 'Türkisch',
  ar: 'Arabisch',
  zh: 'Chinesisch',
};

interface Props {
  doc: DocumentRow;
  missingLangs: string[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function TranslateDocumentDialog({ doc, missingLangs, open, onOpenChange }: Props) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const renderInput = useDocumentRenderInput(open ? doc.id : null);
  const [picked, setPicked] = useState<Set<string>>(() => new Set(missingLangs));
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number; lang?: string } | null>(
    null,
  );

  // Re-seed picks when the dialog reopens with a different missing set
  useEffect(() => {
    if (open) setPicked(new Set(missingLangs));
  }, [open, missingLangs]);

  const togglePick = (lang: string) => {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(lang)) next.delete(lang);
      else next.add(lang);
      return next;
    });
  };

  const allPicked = useMemo(
    () => missingLangs.every((l) => picked.has(l)) && picked.size > 0,
    [missingLangs, picked],
  );
  const togglePickAll = () => {
    setPicked((prev) => (allPicked ? new Set() : new Set(missingLangs)));
  };

  const onSubmit = async () => {
    if (!renderInput.data || picked.size === 0) return;
    setBusy(true);
    setProgress({ done: 0, total: picked.size });
    try {
      const pdf = await import('@/pdf/offerPdfGenerator');
      // The orchestrator always renders DE first as the master; we filter
      // it out of the picked set since we only want missing langs added.
      const langsToRender = ['de', ...Array.from(picked).filter((l) => l !== 'de')];
      // Die Route hängt an `snakeCaseResponse`, ihre obersten Schlüssel
      // kommen also snake_case an. `normalizeRenderInput` benennt ALLE um;
      // die frühere Liste einzelner Felder verlor still die FAGG-Aufforderung,
      // die Bestätigungen aus Punkt 10 und den Gerichtsstand — und der
      // deutsche Master wird hier neu hochgeladen, also stand die Wahl der
      // Maklerin danach nicht mehr im Vertrag.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const data: any = normalizeRenderInput(
        renderInput.data.data as Record<string, unknown>,
      );
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const langs = langsToRender as any;
      let result;
      if (renderInput.data.type === 'vermittlungsauftrag') {
        result = await pdf.generateAllVermittlungsauftrag(doc.id, data, {
          languages: langs,
          onProgress: (lang, done, total) => setProgress({ done, total, lang }),
          onError: (lang, err) => console.warn(`[translate] ${lang} failed`, err),
        });
      } else if (renderInput.data.type === 'besichtigungsschein') {
        result = await pdf.generateAllBesichtigungsschein(doc.id, data, {
          languages: langs,
          onProgress: (lang, done, total) => setProgress({ done, total, lang }),
          onError: (lang, err) => console.warn(`[translate] ${lang} failed`, err),
        });
      } else {
        // Kaufanbot lives in offer_attachments today, not the documents
        // table — retroactive translation for it is a separate workstream
        // and won't reach this branch via the AppointmentDocuments chip
        // strip. Defensive guard in case a future caller wires it up.
        result = await pdf.generateAllKaufanbot(doc.id, data, {
          languages: langs,
          onProgress: (lang, done, total) => setProgress({ done, total, lang }),
          onError: (lang, err) => console.warn(`[translate] ${lang} failed`, err),
        });
      }
      // Wait for the queue to finish before closing — avoid a "translate
      // 5 langs" click that returns to a chip strip showing only DE+EN.
      await result.backgroundPromise;
      qc.invalidateQueries({ queryKey: ['documents'] });
      toast.success(
        t('docs.translate.done', '{{count}} Sprache(n) erfolgreich übersetzt.', {
          count: picked.size,
        }),
      );
      onOpenChange(false);
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusy(false);
      setProgress(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display">
            {t('docs.translate.title', 'Übersetzungen nachträglich erstellen')}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 text-sm">
          <p className="text-ink-2">
            {t(
              'docs.translate.intro',
              'Wählen Sie die Sprachen, die diesem Dokument hinzugefügt werden sollen. Pro Sprache wird derselbe Inhalt mit demselben PDF-Layout gerendert.',
            )}
          </p>

          {doc.signed === 1 ? (
            <div className="flex items-start gap-2 rounded-md border border-[hsl(var(--brand-gold))]/40 bg-[hsl(var(--brand-gold))]/5 p-3 text-xs text-ink-2">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-[hsl(var(--brand-gold-deep))] mt-0.5" />
              <span>
                {t(
                  'docs.translate.signed_warning',
                  'Dokument ist unterschrieben — die Unterschrift wird in jede Übersetzung eingebettet. Aktuelle Stamm­daten werden verwendet (Adresse, Preis), nicht die zum Zeitpunkt der Unterschrift gültigen.',
                )}
              </span>
            </div>
          ) : (
            <p className="rounded-md border border-line bg-paper-2 p-3 text-xs text-ink-3">
              {t(
                'docs.translate.fresh_data_hint',
                'Übersetzung verwendet die aktuellen Stamm­daten von Klient und Immobilie. Hat sich seit der ursprünglichen Erstellung etwas geändert, enthält die Übersetzung den neuen Wert.',
              )}
            </p>
          )}

          {renderInput.isLoading ? (
            <div className="flex items-center justify-center py-8 text-ink-3">
              <Loader2 className="h-4 w-4 animate-spin me-2" />
              {t('common.loading', 'Lädt …')}
            </div>
          ) : renderInput.error ? (
            <div className="rounded-md border border-[hsl(var(--negative))]/30 bg-[hsl(var(--negative))]/5 p-3 text-xs text-[hsl(var(--negative))]">
              {(renderInput.error as Error).message}
            </div>
          ) : (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium uppercase tracking-wide text-ink-3">
                  {t('docs.translate.langs', 'Fehlende Sprachen')} ({missingLangs.length})
                </span>
                <button
                  type="button"
                  onClick={togglePickAll}
                  className="text-xs text-ink-2 underline-offset-2 hover:underline"
                >
                  {allPicked
                    ? t('common.unselect_all', 'Alle abwählen')
                    : t('common.select_all', 'Alle auswählen')}
                </button>
              </div>
              <div className="grid grid-cols-2 gap-1.5">
                {missingLangs.map((lang) => (
                  <label
                    key={lang}
                    className={
                      'flex items-center gap-2 rounded-md border px-2.5 py-1.5 text-sm cursor-pointer transition ' +
                      (picked.has(lang)
                        ? 'border-[hsl(var(--brand-gold))] bg-[hsl(var(--brand-gold)/.08)]'
                        : 'border-line bg-card hover:bg-paper-2')
                    }
                  >
                    <input
                      type="checkbox"
                      checked={picked.has(lang)}
                      onChange={() => togglePick(lang)}
                      disabled={busy}
                      className="accent-[hsl(var(--brand-gold))]"
                    />
                    <span className="font-mono text-xs uppercase font-bold text-ink-3 w-6">
                      {lang}
                    </span>
                    <span className="text-ink">
                      {t(`docs.langs.${lang}`, LANG_LABEL[lang] ?? lang)}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {progress && (
            <div className="flex items-center gap-2 rounded-md border border-line bg-paper-2 p-2 text-xs text-ink-2">
              <Loader2 className="h-3 w-3 animate-spin" />
              {t('docs.translate.progress', 'Sprache {{done}}/{{total}}', {
                done: progress.done,
                total: progress.total,
              })}
              {progress.lang ? ` · ${LANG_LABEL[progress.lang] ?? progress.lang}` : ''}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" disabled={busy} onClick={() => onOpenChange(false)}>
            {t('common.cancel', 'Abbrechen')}
          </Button>
          <Button
            disabled={busy || picked.size === 0 || renderInput.isLoading}
            onClick={onSubmit}
          >
            {busy
              ? t('common.processing', 'Wird verarbeitet …')
              : t('docs.translate.cta', 'Übersetzen ({{count}})', { count: picked.size })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
